import { Formik } from 'formik';
import i18n from 'i18n';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DefaultModalProps as LocationStateModalProps,
  withLocationStateModal,
} from 'utils/modals';
import { formatName } from 'utils/talent';
import * as yup from 'yup';

import { Box, Button, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';

import {
  useCreateReportMutation,
  ReportReasonsEnum,
} from '@libs/graphql-types';
import { ConnectedSelect } from '@libs/ui/components/form/select';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import { DefaultModal } from '@libs/ui/components/modals';
import Typography from '@libs/ui/components/typography';

import {
  REPORT_TALENT_MODAL_ID,
  REPORT_TALENT_REASONS,
  ReportTalentModalData,
} from './types';

const useStyles = makeStyles((theme) => ({
  modal: {
    width: 550,
  },
}));

type FormState = {
  reason: ReportReasonsEnum;
  message: string;
};

type Props = LocationStateModalProps<ReportTalentModalData>;

const validationSchema = yup.object().shape({
  reason: yup
    .string()
    .required(
      i18n.t('viewProfile.report.reasonRequired', { ns: 'talentProfile' }),
    ),
  message: yup.string().optional(),
});

const ReportTalentModalComponent = ({ isOpen, close, modalData }: Props) => {
  const { t } = useTranslation('talentProfile');
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [createReport, { loading }] = useCreateReportMutation({
    onCompleted: () => {
      enqueueSnackbar(t('viewProfile.report.submitSuccess'), {
        variant: 'success',
      });
      close();
    },
    onError: (error) => {
      console.error('Error submitting report:', error);
      enqueueSnackbar(t('viewProfile.report.submitFailed'), {
        variant: 'error',
      });
    },
  });

  const talent = modalData?.talent;
  const talentName = formatName({
    firstName: talent?.first_name_abac?.value,
    lastName: talent?.last_name_abac?.value,
  });

  const initialValues: FormState = {
    reason: '' as ReportReasonsEnum,
    message: '',
  };

  const handleSubmit = (values: FormState) => {
    if (!talent?.id) {
      enqueueSnackbar(t('viewProfile.report.talentNotFound'), {
        variant: 'error',
      });
      return;
    }

    createReport({
      variables: {
        reported_talent_id: talent.id,
        reason: values.reason,
        comment: values.message || undefined,
      },
    });
  };

  return (
    <Formik<FormState>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ handleSubmit }) => (
        <DefaultModal
          handleClose={close}
          open={isOpen}
          className={classes.modal}
          title={
            <Box mb={2} textAlign="left">
              <Typography
                variant="h5"
                fontWeight={500}
                textAlign="center"
                paragraph
              >
                {t('viewProfile.report.title')}
              </Typography>
              <Typography variant="body1" textAlign="center" fontWeight={400}>
                {t('viewProfile.report.subtitle')}
              </Typography>
            </Box>
          }
          actions={
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  color="warning"
                  variant="contained"
                  onClick={() => handleSubmit()}
                  disabled={loading}
                >
                  {loading
                    ? t('viewProfile.report.submitting')
                    : t('viewProfile.report.submit')}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={close}
                  disabled={loading}
                >
                  {t('viewProfile.report.cancel')}
                </Button>
              </Grid>
            </Grid>
          }
        >
          <Box mt={2} textAlign="left">
            <ConnectedSelect
              name="reason"
              hideNoneValue
              fullWidth
              options={REPORT_TALENT_REASONS}
              variant="filled"
              label={t('viewProfile.report.reasonLabel')}
              size="small"
            />
          </Box>
          <Box mt={4} textAlign="left">
            <ConnectedTextField
              name="message"
              label={t('viewProfile.report.additionalDetails')}
              placeholder={t('viewProfile.report.describePlaceholder')}
              fullWidth
              multiline
              minRows={3}
              variant="filled"
            />
          </Box>
        </DefaultModal>
      )}
    </Formik>
  );
};

export const ReportTalentModal = withLocationStateModal<ReportTalentModalData>({
  id: REPORT_TALENT_MODAL_ID,
  getModalKey: (data) => data.talent?.id || '',
})(ReportTalentModalComponent);
